// ============================================================================
// themeCoquille.js — thème jour / nuit : lecture, écriture, application
// ============================================================================
// Déplacé d'appShared.js le 15/10/2026 pour BRISER UN CYCLE D'IMPORT qui
// cassait le serveur de développement :
//
//   uxComponents → toasts → sons → preferencesCoquille → appShared → uxComponents
//
// Au démarrage, `npm start` échouait sur « Cannot access 'toast' before
// initialization » : uxComponents réexporte `toast` (ligne 29), et React
// Refresh enregistre les exports d'un module AVANT que la boucle d'imports
// soit retombée sur ses pieds — la liaison était encore en zone morte. La
// production, qui n'a pas React Refresh, n'était pas touchée : seuls ceux qui
// lançaient le serveur de développement voyaient l'écran rouge.
//
// Le maillon fautif était `preferencesCoquille → appShared` : trois fonctions
// de thème, qui ne tiennent qu'à `localStorage` et à une classe sur <body>,
// vivaient dans un module qui importe, lui, des composants d'interface
// (useConfirm, ModalShell). Elles vivent ici désormais ; `appShared` les
// RÉEXPORTE, donc aucun appelant n'a changé.
//
// Règle à tenir : ce fichier ne doit importer que React. Lui ajouter un
// import d'interface rouvrirait le cycle. Verrou : src/cyclesImports.test.js.
// ============================================================================

import { useLayoutEffect } from "react";

// -- Thème jour / nuit -------------------------------------------------------
export const getTheme = () => { try{return localStorage.getItem("eniovote_theme")||"light";}catch{return "light";} };
// Pages publiques (décision D-L12-3 de Pierre, 29/09/2026) : un premier
// visiteur, sans thème mémorisé, suit le réglage de son téléphone.
export const themePagePublique = () => { try{return localStorage.getItem("eniovote_theme")||"system";}catch{return "system";} };
export const setThemeLS = (t) => { try{localStorage.setItem("eniovote_theme",t);}catch{}};
export const applyTheme = (t) => {
  // Chantier 3 (6/07/2026) : applyTheme ne PORTE PLUS aucune valeur de
  // couleur. Les deux thèmes complets vivent dans src/theme.css (source de
  // vérité unique — body.theme-dark / body.theme-light) ; ici on ne fait que
  // basculer la classe. Historique : cette fonction écrasait ~20 variables en
  // styles inline (setProperty), créant une double source fragile — on purge
  // aussi les éventuels styles inline hérités d'une ancienne session.
  const themeVars = ["--bg-app","--bg-card","--bg-card-dark","--bg-input",
    "--bg-input-border","--text-main","--text-sub","--border-color",
    "--border-input","--sep-color","--btn-ghost-bg","--btn-ghost-color",
    "--btn-ghost-border","--ptab-bg","--ptab-btn-color","--ptab-btn-act-bg",
    "--vrow-bg","--ptrack-bg","--shadow","--card-inner","--navy","--navy-l"];
  themeVars.forEach(v => { try { document.documentElement.style.removeProperty(v); } catch {} });
  // « system » (réglage « Système » de Moi, lot L6) : le thème suit le système
  // (preferencesCoquille.suivreThemeSysteme réapplique au changement).
  const r = t === "system"
    ? ((typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light")
    : t;
  const cls = r === "light" ? " theme-light" : " theme-dark";
  document.body.className = document.body.className.replace(/theme-\w+/g, "").trim() + cls;
};

// D-L12-3 étendue (lot L12h-bis) aux pages publiques montées par App :
// /repondre, /reset, document ouvert sans session, carte « Session expirée ».
// Tant qu'une est affichée, le thème est themePagePublique() (un thème
// mémorisé prime ; sinon le réglage du téléphone, suivi s'il change) ;
// l'application connectée garde getTheme(). L'effet de démarrage d'App passe
// APRÈS les effets de ses enfants : il lit themeEnVigueur(), qui sait qu'une
// page publique est montée, au lieu d'écraser son thème.
let pagesPubliquesMontees = 0;
export const themeEnVigueur = () => (pagesPubliquesMontees > 0 ? themePagePublique() : getTheme());
export function useThemePagePublique(actif = true) {
  useLayoutEffect(() => {
    if (!actif) return undefined;
    pagesPubliquesMontees += 1;
    applyTheme(themePagePublique());
    const maj = () => { if (themePagePublique() === "system") applyTheme("system"); };
    let mq = null;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
      if (mq.addEventListener) mq.addEventListener("change", maj); else if (mq.addListener) mq.addListener(maj);
    } catch { mq = null; /* navigateur sans matchMedia : le thème posé reste */ }
    return () => {
      if (mq) { if (mq.removeEventListener) mq.removeEventListener("change", maj); else if (mq.removeListener) mq.removeListener(maj); }
      pagesPubliquesMontees -= 1;
      if (pagesPubliquesMontees === 0) applyTheme(getTheme());
    };
  }, [actif]);
}
